import React, { useEffect, useState, useCallback, useRef } from "react";
import * as WebBrowser from "expo-web-browser";
import { Share } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  View,
  Platform,
  Linking,
  Pressable,
  NativeEventEmitter,
  NativeModules,
  ActivityIndicator,
} from "react-native";
import Text from "@/components/shared/AppText";

import { useNavigation, useRoute } from "@react-navigation/native";
import { Dropdown } from "react-native-element-dropdown";
import {
  GetAppStore,
  GetMacApp,
  GetPlayStore,
} from "@/components/shared/Buttons";
import tw from "twrnc";
import { Ionicons } from "@expo/vector-icons";

import Button from "@/components/settings/Button";
import { FieldLabel } from "@/components/settings/Field";
import HelpTip from "@/components/settings/HelpTip";
import { SectionTitle, SettingsPage } from "@/components/settings/SettingsPage";
import {
  colors,
  control,
  radius,
  text as settingsText,
} from "@/components/settings/theme";
import RtmpForm, { UrlToWatch } from "./RtmpForm";
import QRCode from "react-qr-code";
import useClipboardCallback from "@/components/hooks/useClipboardCallback";
import { makeUrl, roomNounText, eventTitle } from "@/components/globalUtils";
import { useAuth } from "@/components/AuthContext";
import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import { TextLink } from "@/components/shared/TextLink";
import { createTest } from "@/components/http/events";
import OnboardingButton from "@/components/shared/OnboardingButton";
import ShareEventButton from "@/components/shared/ShareEventButton";
import { openStreamWindow } from "@/components/Utils";
import { downloadMacApp, isMacBrowser } from "@/components/macos/macApp";
import {
  desktopStudioLabel,
  desktopStudioPlatform,
  updateDesktopStudioEvent,
  useDesktopStudioClosed,
} from "@/components/desktop/desktopApp";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import NewTestButton from "@/components/shared/NewTestButton";
import { isBrowserStreamSupported } from "@/components/streaming/capabilities";
import BrowserStreamStudio from "@/components/streaming/BrowserStreamStudio";
import GoLiveWindowNotice, {
  goLiveWindow,
} from "@/components/streaming/GoLiveWindowNotice";
import useNow from "@/components/hooks/useNow";
import RoomSwitcher from "@/components/rooms/RoomSwitcher";
import useRoomGroup from "@/components/rooms/useRoomGroup";
import { useFrame } from "@/components/layout/FrameContext";
import { isGrouped, roomLabelOf, roomNounOf } from "@/components/rooms/rooms";
import { moveEventScreenBelow } from "@/components/rooms/switchInPlace";
import CameraLinkCard from "@/components/rooms/CameraLinkCard";
import { isCameraGroup, isExtraCamera } from "@/components/rooms/multicam";
import { t, tRich } from "@/localization";

// The room noun inside a sentence ("Which stage is this device streaming?").
const nounInSentence = (event) => roomNounText(roomNounOf(event).toLowerCase());

const HowToStream = ({ event, relatedEvent }) => {
  // Rooms: each streams with its own key; switch to the one this device streams.
  const { primary: roomPrimary, rooms: groupRooms } = useRoomGroup(event);
  const { stream_key } = event;
  // Your test is always yours (createTest); event links use the custom
  // domain of the event's account (Teams: the one you work in).
  const {
    broadcaster,
    profileLoaded,
    prefer_multiple_tests,
    getOpenAppFromWebUrl,
  } = useAuth();
  const { custom_domain } = useEventAuth(event);

  const eventToUseForPaywall = relatedEvent || event;

  const [testEvent, setTestEvent] = useState(null);
  // The QR code and "Open our App" open this exact room in the app, so a link
  // fetched for another room (before switching rooms in place) is never shown.
  const [magicLink, setMagicLink] = useState({ slug: null, url: "" });
  const magicUrl = magicLink.slug === event?.slug ? magicLink.url : "";
  const [selectedOption, setSelectedOption] = useState(null);

  const isMobileUX = useIsMobileUX();

  const broadcastLink = `https://eventlive.app.link/streamFor?streamKey=${stream_key}&$ios_nativelink=true`;

  const clipboardCallback = useClipboardCallback();

  const navigation = useNavigation();
  const route = useRoute();
  // Wide layouts: switch rooms the way the event menu's chips do.
  const frame = useFrame();

  // Phones: this screen, and the event screen it was opened from, now show
  // the other room, so Back lands on that room (as on wide layouts).
  const switchRoomInPlace = (member) => {
    moveEventScreenBelow(navigation, route.key, event.slug, member);
    navigation.setParams({ slug: member.slug, event: undefined });
  };

  // EventLive for Mac and EventLive for Windows stream from their native
  // Studio window ("mac" / "windows"); the browser engine is not offered there.
  const desktopStudio = desktopStudioPlatform();
  const onDesktopStudio = desktopStudio !== null;
  // In a browser on a Mac, the EventLive app includes EventLive for Mac.
  const onMacBrowser = isMacBrowser();
  // Each desktop app keeps its own choice, so one saved before its Studio
  // option existed doesn't hide it.
  const optionStorageKey =
    desktopStudio === "mac"
      ? "selectedOptionMac"
      : desktopStudio === "windows"
      ? "selectedOptionWindows"
      : "selectedOption";
  const options = [
    ...(onDesktopStudio
      ? [
          {
            label: desktopStudioLabel({ recommended: true }),
            value: desktopStudio,
          },
        ]
      : []),
    {
      label: onDesktopStudio
        ? t("EventLive App (iPhone, Android)")
        : t("EventLive App (Recommended)"),
      value: "app",
    },
    ...(Platform.OS === "web" && !onDesktopStudio && isBrowserStreamSupported()
      ? [{ label: t("This Browser (Beta)"), value: "browser" }]
      : []),
    { label: "RTMP", value: "rtmp" },
    { label: "OBS", value: "obs" },
    { label: "GoPro", value: "gopro" },
    { label: t("Camera"), value: "camera" },
    { label: "YoloBox", value: "yolobox" },
  ];

  const rtmpsServer = "rtmps://go.eventlive.pro:443/live";

  // Cameras are angles of one event: guests watch every camera on the main
  // camera's link.
  const cameras = isCameraGroup(event);
  const url = makeUrl(
    isExtraCamera(event) && roomPrimary ? roomPrimary : event,
    custom_domain
  );
  // The browser studio only works inside the go-live window (start −1 h … +24 h).
  const now = useNow();
  const browserWindow = goLiveWindow(event, now);

  const linkPress = useCallback(() => {
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else Linking.openURL(url);
  }, [url]);

  const isLive = event?.event_status === "live";

  const openLink = async (url) => {
    await WebBrowser.openBrowserAsync(url);
  };

  const openAppStore = async () => {
    // In EventLive for Mac or Windows the magic link would open this event on
    // the computer itself.
    await openLink(
      (!onDesktopStudio && magicUrl) ||
        "https://apps.apple.com/us/app/eventlive-event-live/id1457137042#?platform=iphone"
    );
  };

  const openPlayStore = async () => {
    await openLink(
      "https://play.google.com/store/apps/details?id=pro.eventlive&hl=en&gl=US"
    );
  };

  useEffect(() => {
    if (Platform.OS === "web" && event && event.slug) {
      let current = true;
      const fetchMagicUrl = async () => {
        const redir = await getOpenAppFromWebUrl(`/getapp/${event.slug}`);
        if (current) setMagicLink({ slug: event.slug, url: redir });
      };

      fetchMagicUrl();
      return () => {
        current = false;
      };
    }
  }, [event?.slug]);

  useEffect(() => {
    (async () => {
      if (broadcaster) {
        const e = await createTest(broadcaster, false);
        setTestEvent(e);
      }
    })();
  }, [broadcaster]);

  useEffect(() => {
    const loadSelectedOption = async () => {
      try {
        const value = await AsyncStorage.getItem(optionStorageKey);
        const browserUnavailable =
          value === "browser" &&
          (Platform.OS !== "web" ||
            onDesktopStudio ||
            !isBrowserStreamSupported());
        // "mac" and "windows" only exist in their own desktop app.
        const studioUnavailable =
          (value === "mac" || value === "windows") && value !== desktopStudio;
        if (value !== null && !browserUnavailable && !studioUnavailable) {
          setSelectedOption(value);
        } else {
          setSelectedOption(desktopStudio || "app");
        }
      } catch (error) {
        console.error("Error loading selected option:", error);
        setSelectedOption("app");
      }
    };

    loadSelectedOption();
  }, []);

  const handleOptionChange = async (item) => {
    try {
      setSelectedOption(item.value);
      await AsyncStorage.setItem(optionStorageKey, item.value);
    } catch (error) {
      console.error("Error saving selected option:", error);
    }
  };

  // Mac and Windows apps: the Studio window shows viewers/status from this
  // page's subscription, and closing it continues like the phones' StreamDone.
  useEffect(() => {
    updateDesktopStudioEvent(event);
  }, [
    event?.stream_key,
    event?.liveViewers,
    event?.event_status,
    event?.title,
  ]);

  useDesktopStudioClosed(event?.stream_key, ({ wentLive }) => {
    if (!wentLive) return;
    if (eventToUseForPaywall && !eventToUseForPaywall.is_test) {
      navigation.replace("PayWall", {
        event: eventToUseForPaywall,
        slug: eventToUseForPaywall.slug,
      });
    } else {
      navigation.popToTop();
    }
  });

  const listenerRef = useRef(null);

  // Effect for adding the event listener
  useEffect(() => {
    const addEventListener = async () => {
      if (
        event &&
        event.stream_key &&
        Platform.OS !== "web" &&
        !listenerRef.current
      ) {
        const emitter =
          Platform.OS === "ios"
            ? new NativeEventEmitter(NativeModules.StartStream)
            : new NativeEventEmitter(NativeModules.StreamModule);

        console.log("adding listener");
        if (listenerRef.current) {
          console.log("Remove current listener");
          listenerRef.current.remove();
        }
        listenerRef.current = emitter.addListener(
          "StreamDone",
          async (data) => {
            console.log("STREAM DONE HowToStream", data, event);
            const reviewAsked = await AsyncStorage.getItem("reviewAsked");
            if (!reviewAsked) {
              navigation.navigate("AskReview", {
                relatedEvent: eventToUseForPaywall,
              });
              await AsyncStorage.setItem("reviewAsked", "true");
            } else {
              console.log("rel-event", eventToUseForPaywall);
              if (eventToUseForPaywall && !eventToUseForPaywall.is_test) {
                navigation.replace("PayWall", {
                  event: eventToUseForPaywall,
                  slug: eventToUseForPaywall.slug,
                });
              } else {
                navigation.popToTop();
              }
            }
          }
        );
      }
    };

    addEventListener();

    return () => {
      if (listenerRef.current) {
        console.log("removing listener");
        listenerRef.current.remove();
        listenerRef.current = null;
      }
    };
  }, [event?.stream_key, relatedEvent?.slug]);

  // Sent to the guest broadcaster, in the sender's language.
  const getInstructions = () => {
    const settings = { server: rtmpsServer, key: event.stream_key, url };
    switch (selectedOption) {
      case "mac":
      case "windows":
      case "app":
        return t(
          "Download the EventLive App and use this link to go live: {link}\n\nLink to watch the event: {url}",
          { link: broadcastLink, url }
        );
      case "browser":
        return t(
          "Go live from your browser (no app needed) using this link: {link}\n\nLink to watch the event: {url}",
          {
            link: `https://broadcaster.eventlive.pro/stream/${event.stream_key}`,
            url,
          }
        );
      case "rtmp":
        return t(
          "Use these RTMP settings to go live:\nURL: {server}\nStream Key: {key}\n\nLink to watch the event: {url}",
          settings
        );
      case "obs":
        return t(
          "Set up OBS with these settings:\nService: Custom\nServer: {server}\nStream Key: {key}\n\nLink to watch the event: {url}",
          settings
        );
      case "gopro":
        return t(
          "Set up your GoPro with these RTMP settings:\nRTMP URL: {server}\nStream Key: {key}\n\nLink to watch the event: {url}",
          settings
        );
      case "camera":
        return t(
          "Connect your camera to a computer or encoder and use these RTMP settings:\nURL: {server}\nStream Key: {key}\n\nLink to watch the event: {url}",
          settings
        );
      case "yolobox":
        return t(
          "Configure your YoloBox with these Custom RTMP settings:\nRTMP URL: {server}\nStream Key: {key}\n\nLink to watch the event: {url}",
          settings
        );
      default:
        return t("Instructions for going live on EventLive");
    }
  };

  const shareInstructions = async () => {
    const instructions = getInstructions();
    if (Platform.OS === "web") {
      if (navigator.share) {
        try {
          await navigator.share({
            title: t("EventLive Streaming Instructions for '{title}'", {
              title: eventTitle(event),
            }),
            text: instructions,
          });
        } catch (error) {
          console.error("Error sharing:", error);
          clipboardCallback(instructions);
        }
      } else {
        clipboardCallback(instructions);
      }
    } else {
      try {
        await Share.share({
          message: instructions,
          url: broadcastLink,
        });
      } catch (error) {
        console.error("Error sharing:", error);
        clipboardCallback(instructions);
      }
    }
  };

  if (selectedOption === null) {
    return (
      <View style={tw`m-5`}>
        <Text>{t("Loading...")}</Text>
      </View>
    );
  }

  return (
    <View>
      <SettingsPage
        title={
          event.is_test
            ? t("How would you like to test?")
            : t("How would you like to Go Live?")
        }
      />
      {isGrouped(event) && (
        <View style={tw`mb-5 -mt-2`}>
          {/* Wide layouts already show the room chips above the event menu. */}
          {frame?.kind !== "pane" && (
            <>
              <FieldLabel
                label={
                  cameras
                    ? t("Which camera is this phone?")
                    : t("Which {noun} is this device streaming?", {
                        noun: nounInSentence(roomPrimary || event),
                      })
                }
              />
              <RoomSwitcher
                current={event}
                primary={roomPrimary}
                rooms={groupRooms}
                onSelect={frame?.switchRoom || switchRoomInPlace}
              />
            </>
          )}
          <View
            style={[
              tw`flex-row items-center`,
              frame?.kind !== "pane" && tw`mt-1.5`,
            ]}
          >
            <Text style={[settingsText.hint, tw`flex-shrink`]}>
              {cameras
                ? t(
                    "You're setting up {room}. Each camera streams from its own phone and keeps its own recording.",
                    { room: roomLabelOf(event) }
                  )
                : t(
                    "You're setting up {room}. Every {noun} needs its own phone, camera or OBS.",
                    {
                      room: roomLabelOf(event),
                      noun: nounInSentence(roomPrimary || event),
                    }
                  )}
            </Text>
            <HelpTip topic="goLive.room" />
          </View>
          {cameras && event.is_published && !event.is_test && (
            <CameraLinkCard event={event} style={tw`mt-4`} />
          )}
          {cameras && !event.is_published && (
            <Text style={[settingsText.hint, tw`mt-3`]}>
              {t("Unlock the cameras on the event screen to stream {room}.", {
                room: roomLabelOf(event),
              })}
            </Text>
          )}
        </View>
      )}
      <FieldLabel label={t("Stream from")} style={tw`-mt-2`} />
      <Dropdown
        data={options}
        labelField="label"
        valueField="value"
        value={selectedOption}
        onChange={(item) => {
          handleOptionChange(item);
        }}
        style={[
          tw`mb-5 px-3.5 bg-white`,
          {
            height: control.lg,
            borderWidth: 1,
            borderColor: colors.border,
            borderRadius: radius.control,
          },
        ]}
        selectedTextStyle={settingsText.body}
        itemTextStyle={settingsText.body}
        containerStyle={tw`p-1 border border-gray-300 rounded-lg`}
      />

      {/* The browser studio stays mounted while live — it IS the live UI —
          so it renders outside the isLive/!isLive split. */}
      {selectedOption === "browser" && !event.stream_key && (
        <View style={tw`py-10 items-center`}>
          <ActivityIndicator />
        </View>
      )}
      {selectedOption === "browser" &&
        Boolean(event.stream_key) &&
        (event.is_test || event.is_published ? (
          <View style={tw`mb-10`}>
            {browserWindow.open ? (
              <BrowserStreamStudio
                event={event}
                streamKey={event.stream_key}
                isTest={Boolean(event.is_test)}
                watchUrl={url}
              />
            ) : (
              <GoLiveWindowNotice
                event={event}
                window={browserWindow}
                onTest={() =>
                  navigation.navigate("BrowserStream", {
                    slug: event.slug,
                    variant: "test",
                  })
                }
                onChangeDate={() =>
                  navigation.navigate("EventDetails", {
                    slug: event.slug,
                    event,
                  })
                }
              />
            )}
          </View>
        ) : (
          <View style={tw`mb-10 flex-col gap-3`}>
            <OnboardingButton
              text={t("Start free test in browser")}
              icon="videocam-outline"
              bordered
              dark
              onPress={() =>
                navigation.navigate("BrowserStream", {
                  slug: event.slug,
                  variant: "test",
                })
              }
            />
            <OnboardingButton
              text={t("Unlock event")}
              onPress={() =>
                navigation.navigate("PayWall", {
                  event: eventToUseForPaywall,
                  slug: eventToUseForPaywall.slug,
                })
              }
            />
          </View>
        ))}

      {isLive && selectedOption !== "browser" && (
        <View style={tw`p-5 flex-1`}>
          <Text style={[settingsText.sectionTitle, tw`mb-2.5`]}>
            {event.is_test ? t("Your test is streaming") : t("You are Live!")}
          </Text>

          <View
            style={tw`flex-1 justify-center items-center self-center max-w-[800px]`}
          >
            <OnboardingButton
              onPress={linkPress}
              text={event.is_test ? t("Watch your test") : t("Watch Live")}
              style={tw`w-full`}
            />

            <ShareEventButton
              url={url}
              title={eventTitle(event)}
              text={t("Share")}
              dark
              bordered
              style={tw`w-full self-center`}
            />

            <TextLink
              text={url}
              onPress={linkPress}
              textStyle={tw`text-sm py-1.5`}
            />
          </View>
        </View>
      )}
      {!isLive && (
        <>
          {onDesktopStudio && selectedOption === desktopStudio && (
            <View style={tw`mb-10`}>
              <Text style={tw`py-2.5`}>
                {desktopStudio === "windows"
                  ? t(
                      "Go live right from this PC. The Studio window works with the built-in camera and USB cameras — switch cameras and microphones any time, even while you're live. Tests are free."
                    )
                  : t(
                      "Go live right from this Mac. The Studio window works with the built-in camera, USB and HDMI cameras, and your iPhone as a webcam — switch cameras and microphones any time, even while you're live. Tests are free."
                    )}
              </Text>
              {event.is_test || event.is_published ? (
                <OnboardingButton
                  text={event.is_test ? t("Start free test") : t("Go Live")}
                  icon="videocam-outline"
                  onPress={() => openStreamWindow(event)}
                />
              ) : (
                <View style={tw`flex-col gap-3`}>
                  <NewTestButton
                    relatedEvent={eventToUseForPaywall}
                    style={tw`my-2.5 h-[62px]`}
                    openStreamDirectly={true}
                  />
                  <OnboardingButton
                    text={t("Unlock event")}
                    onPress={() =>
                      navigation.navigate("PayWall", {
                        event: eventToUseForPaywall,
                        slug: eventToUseForPaywall.slug,
                      })
                    }
                  />
                </View>
              )}
              <UrlToWatch
                title={
                  event.is_test
                    ? t("Your private test link")
                    : t("Link to watch your live event")
                }
                url={url}
                isTest={!!event.is_test}
              />
            </View>
          )}

          {selectedOption === "app" && (
            <>
              <View style={tw`mb-10`}>
                {Platform.OS !== "web" && (
                  <>
                    <Button
                      text={t("Share link to watch")}
                      icon="share-outline"
                      style={tw`mb-4`}
                      onPress={async () => {
                        try {
                          await Share.share({
                            message: event.is_test
                              ? t("Watch me run this test live: {url}", { url })
                              : t(
                                  "Click to Watch '{title}' event live: {url}",
                                  {
                                    title: eventTitle(event),
                                    url,
                                  }
                                ),
                            url: url,
                          });
                        } catch (error) {
                          console.error("Error sharing:", error);
                          clipboardCallback(url);
                        }
                      }}
                    />

                    {event.is_test || event.is_published ? (
                      <OnboardingButton
                        text={
                          event.is_test ? t("Start free test") : t("Go Live")
                        }
                        icon="videocam-outline"
                        onPress={async () => {
                          await openStreamWindow(event);
                        }}
                      />
                    ) : (
                      <View style={tw`flex-col gap-3`}>
                        <NewTestButton
                          relatedEvent={eventToUseForPaywall}
                          style={tw`my-2.5 h-[62px]`}
                          openStreamDirectly={true}
                        />
                        <OnboardingButton
                          text={t("Unlock event")}
                          onPress={() =>
                            navigation.navigate("PayWall", {
                              event: eventToUseForPaywall,
                              slug: eventToUseForPaywall.slug,
                            })
                          }
                        />
                      </View>
                    )}
                  </>
                )}
                {Platform.OS === "web" && (
                  <>
                    <Text style={tw`py-2.5`}>
                      {onDesktopStudio
                        ? t(
                            "To go live from your iPhone or Android phone, scan this code with the phone's camera: it opens this event in the EventLive app, already signed in. It's recommended to use a tripod for stability. You can start streaming as early as 1 hour before the scheduled time. Tests are free."
                          )
                        : onMacBrowser
                        ? t(
                            "To go Live, all you need is the EventLive App. Use it on your iPhone or Android phone, or download EventLive for Mac to go live from this Mac with its built-in camera, a USB or HDMI camera, or your iPhone as a webcam. It's recommended to use a tripod for stability. You can start streaming as early as 1 hour before the scheduled time. Tests are free."
                          )
                        : t(
                            "To go Live, all you need is the EventLive App. EventLive app is available for iPhone, Android and Mac. It's recommended to use a tripod for stability. You can start streaming as early as 1 hour before the scheduled time. Tests are free."
                          )}
                    </Text>
                    <View style={tw`flex-1 flex-col justify-center`}>
                      {isMobileUX && !onDesktopStudio && magicUrl && (
                        <View>
                          <OnboardingButton
                            text={t("Open our App to Go Live")}
                            onPress={() => {
                              Linking.openURL(
                                magicUrl ||
                                  `https://eventlive.app.link/streamFor?streamKey=${event?.stream_key}&$ios_nativelink=true`
                              );
                            }}
                          />
                        </View>
                      )}
                      {isMobileUX && onMacBrowser && (
                        <GetMacApp
                          onPress={downloadMacApp}
                          style={tw`mt-4 mb-10 self-start`}
                        />
                      )}

                      {(!isMobileUX || onDesktopStudio) && magicUrl && (
                        <View>
                          <Pressable
                            onPress={openAppStore}
                            disabled={onDesktopStudio}
                          >
                            <QRCode
                              value={magicUrl}
                              style={tw`max-w-[200px]`}
                            />
                          </Pressable>
                          <View
                            style={tw`flex-row flex-wrap gap-2.5 mt-10 mb-10`}
                          >
                            <GetAppStore onPress={openAppStore} />
                            <GetPlayStore onPress={openPlayStore} />
                            {onMacBrowser && (
                              <GetMacApp onPress={downloadMacApp} />
                            )}
                          </View>
                        </View>
                      )}
                      <UrlToWatch
                        title={
                          event.is_test
                            ? t("Your private test link")
                            : t("Link to watch your live event")
                        }
                        url={url}
                        isTest={!!event.is_test}
                      />
                    </View>
                  </>
                )}
              </View>
            </>
          )}

          {selectedOption === "rtmp" && (
            <>
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "You can go live from computer, certain cameras, encoders, third party apps and professional equipment using RTMP settings below:"
                  )}
                </Text>

                <RtmpForm
                  event={event}
                  test_event={testEvent}
                  custom_domain={custom_domain}
                />
              </View>
            </>
          )}

          {selectedOption === "obs" && (
            <>
              <View style={tw`mb-10`}>
                {onMacBrowser && (
                  <Text style={tw`py-2.5`}>
                    {tRich(
                      "On a Mac, EventLive for Mac goes live without OBS or a stream key, and switches cameras while you're live. <link>Download for Mac</link>",
                      {
                        link: (words) => (
                          <TextLink onPress={downloadMacApp} text={words} />
                        ),
                      }
                    )}
                  </Text>
                )}
                <Text style={tw`py-2.5`}>
                  {t(
                    "OBS (Open Broadcaster Software) is a free and open-source software for video recording and live streaming. You can use OBS to stream to EventLive using the RTMP settings provided below."
                  )}
                </Text>
                <Text style={tw`py-2.5`}>
                  {t("Follow these steps to set up OBS Studio:")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {tRich(
                    "1. <link>Download and Install OBS Studio</link> on your computer.",
                    {
                      link: (words) => (
                        <TextLink
                          onPress={() =>
                            Linking.openURL("https://obsproject.com/download")
                          }
                          text={words}
                        />
                      ),
                    }
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("2. Open OBS Studio, skip Auto Configuration Wizard.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    "3. Open Settings > Stream > Choose Service > Show All... > EventLive.pro, and enter your stream key."
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    "4. In Sources add (+) a new Video Capture Device. Name it and in Properties choose your Web Camera and appropriate Preset (for example 1280x720)."
                  )}
                </Text>
                <Text style={tw`py-1.5`}>{t("5. Start Streaming")}</Text>

                <RtmpForm
                  event={event}
                  test_event={testEvent}
                  custom_domain={custom_domain}
                />
              </View>
            </>
          )}

          {selectedOption === "camera" && (
            <>
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "You can go live using your camcorder, DSLR, or a webcam. You will need to connect your camera to your computer or an encoder and use the RTMP settings below."
                  )}
                </Text>

                <Text style={tw`py-2.5`}>
                  {t(
                    "Depending on your camera, you may need to install additional drivers or use a capture card device to convert HDMI signal to USB webcam format."
                  )}
                </Text>

                <Text style={tw`py-2.5`}>
                  {tRich(
                    "Read more about <link>how to connect your camera to EventLive</link>.",
                    {
                      link: (words) => (
                        <TextLink
                          onPress={() =>
                            openLink(
                              "https://www.eventlive.pro/blog/live-stream-with-camera"
                            )
                          }
                          text={words}
                        />
                      ),
                    }
                  )}
                </Text>
                <RtmpForm
                  event={event}
                  test_event={testEvent}
                  custom_domain={custom_domain}
                />
              </View>
            </>
          )}

          {selectedOption === "gopro" && (
            <>
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "GoPro cameras (Hero7, Hero8, Hero9, Hero10, GoPro MAX) can be used to stream to EventLive. Follow these steps to go live:"
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("1. Install the Quik app on your phone or tablet.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t('2. Open the Quik app and choose "Control Your GoPro".')}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    '3. Find the "LIVE" button at the bottom of the screen and click "Set Up Live".'
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("4. Select RTMP as your streaming platform.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("5. Enter the RTMP URL provided below.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    '6. Set resolution to 720p and make sure "Save a copy" is ON.'
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    '7. Click "Set Up Live" and then "Go Live" on the next screen.'
                  )}
                </Text>
                <Text style={tw`py-2.5 font-bold`}>
                  {t(
                    "Important: Keep your GoPro close to a WiFi source or your phone if using as a hotspot. Avoid direct sunlight for extended periods to prevent overheating."
                  )}
                </Text>

                <RtmpForm
                  event={event}
                  test_event={testEvent}
                  custom_domain={custom_domain}
                  displayStyle="url"
                />
              </View>
            </>
          )}

          {selectedOption === "yolobox" && (
            <>
              <View style={tw`mb-10`}>
                <Text style={tw`py-2.5`}>
                  {t(
                    "YoloBox can be used to stream to EventLive. Follow these steps to go live:"
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("1. Go to the Connections tab and hit the + icon.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("2. Choose new Custom RTMP.")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    "3. YoloBox will offer to send a special link to your email where you can configure the connection."
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    "4. Follow the link from the email and enter the RTMP details provided below."
                  )}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t("5. Do not check the Authentication checkbox.")}
                </Text>
                <Text style={tw`py-2.5 font-bold`}>
                  {t(
                    "Important: YoloBox may not allow you to enter connections for multiple events. If you need to delete a connection to add a different event's stream key, swipe the connection and choose Delete."
                  )}
                </Text>
                <Text style={tw`py-2.5`}>
                  {t("Troubleshooting tips if your YoloBox stream is laggy:")}
                </Text>
                <Text style={tw`py-1.5`}>
                  {t(
                    "• Update YoloBox software and firmware, use fewer cameras, switch to CBR bitrate mode and lower if needed, set resolution to 720p with 2000-3000kbps bitrate, choose Direct streaming mode in Settings, avoid overlays and extra features, and prevent device overheating. These adjustments can help improve stream quality and reduce lag."
                  )}
                </Text>

                <RtmpForm
                  event={event}
                  test_event={testEvent}
                  custom_domain={custom_domain}
                />
              </View>
            </>
          )}

          {/* Cameras have their own link card at the top instead: this box
              is for one broadcaster at a time. */}
          {!(cameras && isGrouped(event)) && (
            <View
              style={[
                tw`p-4 mb-10`,
                { backgroundColor: colors.surface, borderRadius: radius.card },
              ]}
            >
              <SectionTitle
                title={t("Delegate going live")}
                help="goLive.delegate"
                description={t(
                  "Invite a guest broadcaster, such as a friend or a videographer. They can go live without signing up. Only one broadcaster can stream at a time."
                )}
              />
              {event.is_published && (
                <Button
                  text={t("Share instructions")}
                  icon="share-outline"
                  size="md"
                  fullWidth={false}
                  onPress={shareInstructions}
                />
              )}
              {!event.is_published && (
                <Button
                  text={t("Unlock event to share it")}
                  icon="lock-open-outline"
                  size="md"
                  fullWidth={false}
                  onPress={() =>
                    navigation.navigate("PayWall", { event, slug: event.slug })
                  }
                />
              )}
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default HowToStream;
